import { MoreHorizontal, Eye, Trash2, Edit2 } from "lucide-react";
import { TableActionMenu } from "../ui/TableActionMenu";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "../ui/Card";
import { Checkbox } from "../ui/Checkbox";
import { useAuth } from "../../contexts/AuthContext";

interface Enrollment {
  _id: string;
  user?: {
    _id: string;
    firstName?: string;
    lastName?: string;
    username?: string;
    email?: string;
    profilePicture?: string;
  } | null;
  course: {
    _id: string;
    title: string;
    thumbnail?: string;
  };
  status: string;
  isFree: boolean;
  enrollmentDate: string;
  paymentInfo?: {
    amountPaid: number;
    currency: string;
    paymentMethod: string;
  };
  watchTime?: {
    formatted: string;
    totalSeconds: number;
  };
  enrollmentExpiresAt?: string; // If backend provides it, otherwise we might need to calculate or show N/A
}

interface EnrollmentsListTableProps {
  enrollments: Enrollment[];
  isLoading: boolean;
  onEdit: (enrollment: Enrollment) => void;
  onDelete: (enrollment: Enrollment) => void;
  totalItems: number;
  currentPage: number;
  pageSize: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  selectedIds: Set<string>;
  onSelectChange: (id: string, selected: boolean) => void;
  onSelectAll: (selected: boolean) => void;
  allVisibleSelected: boolean;
  someVisibleSelected: boolean;
}

export function EnrollmentsListTable({
  enrollments,
  isLoading,
  onEdit,
  onDelete,
  totalItems,
  currentPage,
  pageSize,
  totalPages,
  onPageChange,
  selectedIds,
  onSelectChange,
  onSelectAll,
  allVisibleSelected,
  someVisibleSelected,
}: EnrollmentsListTableProps) {
  const { hasPermission } = useAuth();
  const [activeMenu, setActiveMenu] = useState<{
    id: string;
    position: { top: number; right: number };
  } | null>(null);

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "active":
        return "bg-green-100 text-green-700";
      case "completed":
        return "bg-blue-100 text-blue-700";
      case "expired":
      case "failed":
      case "payment failed":
        return "bg-red-100 text-red-700";
      case "suspended":
      case "pending":
        return "bg-amber-100 text-amber-700";
      case "cancelled":
        return "bg-gray-100 text-gray-600";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  /** Prefer payment outcome over raw enrollment status for pending unpaid rows. */
  const getDisplayStatus = (item: {
    status?: string;
    paymentInfo?: { paymentStatus?: string };
  }) => {
    const pay = item.paymentInfo?.paymentStatus;
    if (item.status === "pending" && pay === "failed") return "Payment failed";
    if (item.status === "pending" && pay === "cancelled") return "Cancelled";
    return item.status || "unknown";
  };

  if (isLoading) {
    return (
      <Card className="p-0 border shadow-sm">
        <div className="p-8 text-center text-gray-500">
          <div className="animate-spin inline-block w-6 h-6 border-[3px] border-current border-t-transparent text-indigo-600 rounded-full" />
          <p className="mt-2">Loading enrollments...</p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-0 border shadow-sm h-full flex flex-col">
      {/* Single Scrollable Table View */}
      <div className="overflow-x-auto">
        <table className="min-w-[1200px] w-full text-left text-sm border-separate border-spacing-0">
          <thead className="bg-gray-50 border-b border-gray-100 sticky top-0 z-10">
            <tr>
              <th className="px-4 py-4 w-[50px] bg-gray-50 border-b border-gray-100">
                <Checkbox
                  checked={allVisibleSelected}
                  ref={(input) => {
                    if (input && someVisibleSelected && !allVisibleSelected) {
                      input.indeterminate = true;
                    } else if (input) {
                      input.indeterminate = false;
                    }
                  }}
                  onChange={(e) => onSelectAll(e.target.checked)}
                  className="cursor-pointer"
                />
              </th>
              <th className="px-6 py-4 font-semibold text-gray-900 bg-gray-50 border-b border-gray-100">
                Student Name
              </th>
              <th className="px-6 py-4 font-semibold text-gray-900 bg-gray-50 border-b border-gray-100">
                Course Name
              </th>
              <th className="px-6 py-4 font-semibold text-gray-900 bg-gray-50 border-b border-gray-100">
                Enrol Type
              </th>
              <th className="px-6 py-4 font-semibold text-gray-900 bg-gray-50 border-b border-gray-100">
                Bandwidth Consumption
              </th>
              <th className="px-6 py-4 font-semibold text-gray-900 bg-gray-50 border-b border-gray-100">
                Course Expiry Date
              </th>
              <th className="px-6 py-4 font-semibold text-gray-900 bg-gray-50 border-b border-gray-100">
                Enrollment Date
              </th>
              <th className="px-6 py-4 font-semibold text-gray-900 bg-gray-50 border-b border-gray-100">
                Status
              </th>
              <th className="px-6 py-4 w-[50px] bg-gray-50 border-b border-gray-100"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {enrollments.length === 0 ? (
              <tr>
                <td
                  colSpan={9}
                  className="px-6 py-12 text-center text-gray-500"
                >
                  No enrollments found.
                </td>
              </tr>
            ) : (
              enrollments.map((item) => (
                <tr
                  key={item._id}
                  className={`${
                    selectedIds.has(item._id) ? "bg-blue-50" : "hover:bg-gray-50/50"
                  } transition-colors`}
                >
                  <td className="px-4 py-4">
                    <Checkbox
                      checked={selectedIds.has(item._id)}
                      onChange={() => onSelectChange(item._id, !selectedIds.has(item._id))}
                      className="cursor-pointer"
                    />
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      {item.user?.profilePicture ? (
                        <img
                          src={item.user.profilePicture}
                          alt=""
                          className="w-8 h-8 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-bold">
                          {(
                            item.user?.firstName?.[0] ||
                            item.user?.username?.[0] ||
                            "?"
                          ).toUpperCase()}
                        </div>
                      )}
                      <div className="flex flex-col">
                        <span className="font-medium text-[#102040]">
                          {item.user?.firstName && item.user?.lastName
                            ? `${item.user.firstName} ${item.user.lastName}`
                            : item.user?.username || "Unknown User"}
                        </span>
                        <span className="text-xs text-gray-400">
                          {item.user?.email || "No email"}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 font-medium text-[#102040]">
                    {item.course?.title || "Unknown Course"}
                  </td>
                  <td className="px-6 py-4">
                    {item.isFree ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                        Free
                      </span>
                    ) : (
                      <div className="flex flex-col">
                        <span className="font-medium text-[#102040]">
                          {item.status === "pending" &&
                          !(item.paymentInfo?.amountPaid > 0)
                            ? item.paymentInfo?.originalPrice ??
                              item.paymentInfo?.amountPaid ??
                              0
                            : item.paymentInfo?.amountPaid ?? 0}{" "}
                          {item.paymentInfo?.currency || ""}
                          {item.status === "pending" &&
                            !(item.paymentInfo?.amountPaid > 0) && (
                              <span className="ml-1 text-xs font-normal text-amber-600">
                                (unpaid)
                              </span>
                            )}
                        </span>
                        <span className="text-xs text-gray-400 flex items-center gap-1">
                          {item.paymentInfo?.paymentMethod === "paytabs"
                            ? "PayTabs"
                            : item.paymentInfo?.paymentMethod || "N/A"}
                        </span>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <span className="font-medium text-[#102040]">
                      {item.watchTime?.formatted || "00:00:00"}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-[#989797]">
                    {item.enrollmentExpiresAt
                      ? new Date(item.enrollmentExpiresAt).toLocaleDateString()
                      : "Lifetime"}
                  </td>
                  <td className="px-6 py-4 text-[#989797]">
                    {item.enrollmentDate
                      ? new Date(item.enrollmentDate).toLocaleDateString()
                      : "N/A"}
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getStatusColor(
                        getDisplayStatus(item),
                      )}`}
                    >
                      {getDisplayStatus(item)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right relative">
                    <button
                      className="p-1 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (activeMenu?.id === item._id) {
                          setActiveMenu(null);
                        } else {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setActiveMenu({
                            id: item._id,
                            position: {
                              top: rect.bottom,
                              right: window.innerWidth - rect.right,
                            },
                          });
                        }
                      }}
                    >
                      <MoreHorizontal size={20} />
                    </button>

                    <TableActionMenu
                      isOpen={activeMenu?.id === item._id}
                      onClose={() => setActiveMenu(null)}
                      position={activeMenu?.position || null}
                    >
                      {item.user?._id ? (
                        <Link
                          to={`/students/${item.user._id}`}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                        >
                          <Eye size={14} />
                          View Student
                        </Link>
                      ) : null}
                      {hasPermission('enrollments:update') && (
                        <button
                          onClick={() => {
                            onEdit(item);
                            setActiveMenu(null);
                          }}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                        >
                          <Edit2 size={14} className="text-blue-500" />
                          Edit Enrollment
                        </button>
                      )}
                      {hasPermission('enrollments:delete') && (
                        <button
                          onClick={() => {
                            onDelete(item);
                            setActiveMenu(null);
                          }}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors"
                        >
                          <Trash2 size={14} />
                          Delete
                        </button>
                      )}
                    </TableActionMenu>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalItems > 0 && (
        <div className="px-3 sm:px-6 py-4 border-t border-gray-100 flex items-center justify-between gap-2 text-sm text-gray-500">
          <span>
            Showing {Math.min((currentPage - 1) * pageSize + 1, totalItems)} to{" "}
            {Math.min(currentPage * pageSize, totalItems)} of {totalItems}{" "}
            enrollments
          </span>
          <div className="flex gap-1 ml-4">
            <button
              className="px-2 py-1 hover:bg-gray-100 rounded disabled:opacity-50"
              disabled={currentPage === 1}
              onClick={() => onPageChange(Math.max(1, currentPage - 1))}
            >
              &lt; Previous
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              // Simple pagination logic: show first 5 or window around current
              let p = i + 1;
              if (totalPages > 5 && currentPage > 3) {
                p = currentPage - 2 + i;
              }
              if (p > totalPages) return null;

              return (
                <button
                  key={p}
                  onClick={() => onPageChange(p)}
                  className={`px-3 py-1 rounded ${
                    currentPage === p
                      ? "bg-gray-900 text-white"
                      : "hover:bg-gray-100"
                  }`}
                >
                  {p}
                </button>
              );
            })}
            <button
              className="px-2 py-1 hover:bg-gray-100 rounded disabled:opacity-50"
              disabled={currentPage === totalPages}
              onClick={() =>
                onPageChange(Math.min(totalPages, currentPage + 1))
              }
            >
              Next &gt;
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}
